import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { MultiSelect } from 'primereact/multiselect';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { formatDateOnly, formatDateWithTime } from '../../../../../utils/dateUtils';
import { useGetVehicleRegistrationFormHistoryQuery } from '../../../../../api';

const VehicleRegistrationHistoryDialog = ({ hideDialog, dialogVisible, vehicleRegistrationForm }) => {
    const { th, getCurrentLanguageId } = useLanguage();

    const [historyData, setHistoryData] = useState([]);
    const [visibleColumns, setVisibleColumns] = useState([
        'chassisNo',
        'vehiclePlate',
        'kmInformation',
        'vehicleRegistration',
        'nameCompany',
        'customerPhone',
        'customerEmail',
        'customerCity',
        'customerCountry',
        'returnMsg',
        'status',
        'createdDate',
        'modifiedDate'
    ]);
    const dt = useRef(null);

    const shouldFetch = dialogVisible && vehicleRegistrationForm && vehicleRegistrationForm.id;

    const {
        data: historyRecords,
        isLoading,
        isError,
        error
    } = useGetVehicleRegistrationFormHistoryQuery(
        {
            id: vehicleRegistrationForm?.id,
            pageSize: 50,
            pageNumber: 0,
            languageId: getCurrentLanguageId()
        },
        {
            skip: !shouldFetch,
            refetchOnMountOrArgChange: true
        }
    );

    useEffect(() => {
        if (historyRecords && historyRecords.items) {
            setHistoryData(historyRecords.items);
        } else {
            setHistoryData([]);
        }
    }, [historyRecords]);

    const columnConfigs = [
        {
            field: 'chassisNo',
            header: th('warranty.dealerVehicleRegistrationForm.form.history.chassisNo'),
            sortable: true,
            style: { minWidth: '180px' }
        },
        {
            field: 'vehiclePlate',
            header: th('warranty.dealerVehicleRegistrationForm.form.history.vehiclePlate'),
            sortable: true,
            style: { minWidth: '150px' }
        },
        {
            field: 'kmInformation',
            header: th('warranty.dealerVehicleRegistrationForm.form.history.kmInformation'),
            body: (rowData) => (rowData.kmInformation ? rowData.kmInformation.toLocaleString('tr-TR') + ' km' : ''),
            sortable: true,
            style: { minWidth: '120px' }
        },
        {
            field: 'vehicleRegistration',
            header: th('warranty.dealerVehicleRegistrationForm.form.history.vehicleRegistration'),
            body: (rowData) => formatDateOnly(rowData.vehicleRegistration),
            sortable: true,
            style: { minWidth: '150px' }
        },
        {
            field: 'nameCompany',
            header: th('warranty.dealerVehicleRegistrationForm.form.history.nameCompany'),
            sortable: true,
            style: { minWidth: '200px' }
        },
        {
            field: 'customerPhone',
            header: th('warranty.dealerVehicleRegistrationForm.form.history.customerPhone'),
            sortable: true,
            style: { minWidth: '140px' }
        },
        {
            field: 'customerEmail',
            header: th('warranty.dealerVehicleRegistrationForm.form.history.customerEmail'),
            sortable: true,
            style: { minWidth: '180px' }
        },
        {
            field: 'customerAddress',
            header: th('warranty.dealerVehicleRegistrationForm.form.history.customerAddress'),
            sortable: true,
            style: { minWidth: '200px' }
        },
        {
            field: 'customerStreet',
            header: th('warranty.dealerVehicleRegistrationForm.form.history.customerStreet'),
            sortable: true,
            style: { minWidth: '150px' }
        },
        {
            field: 'customerHome',
            header: th('warranty.dealerVehicleRegistrationForm.form.history.customerHome'),
            sortable: true,
            style: { minWidth: '100px' }
        },
        {
            field: 'customerCity',
            header: th('warranty.dealerVehicleRegistrationForm.form.history.customerCity'),
            sortable: true,
            style: { minWidth: '120px' }
        },
        {
            field: 'customerPostalCode',
            header: th('warranty.dealerVehicleRegistrationForm.form.history.customerPostalCode'),
            sortable: true,
            style: { minWidth: '120px' }
        },
        {
            field: 'customerCountry',
            header: th('warranty.dealerVehicleRegistrationForm.form.history.customerCountry'),
            sortable: true,
            style: { minWidth: '120px' }
        },
        {
            field: 'returnMsg',
            header: th('warranty.dealerVehicleRegistrationForm.form.history.returnMsg'),
            sortable: true,
            style: { minWidth: '200px' }
        },
        {
            field: 'status',
            header: th('warranty.dealerVehicleRegistrationForm.form.history.status'),
            sortable: true,
            style: { minWidth: '120px' }
        },
        {
            field: 'createdDate',
            header: th('warranty.dealerVehicleRegistrationForm.form.history.createdDate'),
            body: (rowData) => formatDateWithTime(rowData.createdDate),
            sortable: true,
            style: { minWidth: '180px' }
        },
        {
            field: 'modifiedDate',
            header: th('warranty.dealerVehicleRegistrationForm.form.history.modifiedDate'),
            body: (rowData) => formatDateWithTime(rowData.modifiedDate),
            sortable: true,
            style: { minWidth: '180px' }
        }
    ];

    const columnOptions = columnConfigs.map((config) => ({
        label: config.header,
        value: config.field
    }));

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    const headerTemplate = () => {
        return (
            <div className="flex flex-column sm:flex-row w-full justify-content-end align-items-stretch sm:align-items-center gap-2">
                <div className="w-full sm:w-auto">
                    <MultiSelect
                        value={visibleColumns}
                        options={columnOptions}
                        onChange={(e) => setVisibleColumns(e.value)}
                        className="w-full"
                        placeholder={th('warranty.dealerVehicleRegistrationForm.form.history.selectColumns')}
                        maxSelectedLabels={3}
                        selectedItemsLabel={th('warranty.dealerVehicleRegistrationForm.form.history.selectedItems')}
                        panelStyle={{ width: '300px' }}
                    />
                </div>
                <Button
                    type="button"
                    icon="pi pi-file"
                    label={th('warranty.dealerVehicleRegistrationForm.form.history.csvExport')}
                    onClick={exportCSV}
                    className="p-button-outlined w-full sm:w-auto"
                    disabled={!historyData || historyData.length === 0}
                />
            </div>
        );
    };

    const renderDialogFooter = () => {
        return (
            <div className="flex justify-content-end gap-2">
                <Button label={th('warranty.dealerVehicleRegistrationForm.form.history.close')} icon="pi pi-times" outlined onClick={hideDialog} type="button" />
            </div>
        );
    };

    return (
        <Dialog
            visible={dialogVisible}
            style={{ width: '90%', maxWidth: '1200px' }}
            header={`${th('warranty.dealerVehicleRegistrationForm.form.history.title')} - ${vehicleRegistrationForm?.chassisNo || ''}`}
            modal
            className="p-fluid"
            footer={renderDialogFooter()}
            onHide={hideDialog}
            maximizable
        >
            {isLoading && (
                <div className="flex justify-content-center">
                    <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
                </div>
            )}

            {isError && <div className="p-error">{th('warranty.dealerVehicleRegistrationForm.form.history.loadingError').replace('{0}', error?.message || th('warranty.dealerVehicleRegistrationForm.form.history.unknownError'))}</div>}

            {!isLoading && !isError && (!historyData || historyData.length === 0) && <div className="text-center p-4">{th('warranty.dealerVehicleRegistrationForm.form.history.noHistoryFound')}</div>}

            {!isLoading && !isError && historyData && historyData.length > 0 && (
                <DataTable
                    ref={dt}
                    value={historyData}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    className="p-datatable-gridlines"
                    showGridlines
                    stripedRows
                    emptyMessage={th('warranty.dealerVehicleRegistrationForm.form.history.emptyMessage')}
                    dataKey="id"
                    tableStyle={{ minWidth: '50rem' }}
                    header={headerTemplate()}
                >
                    {columnConfigs
                        .filter((config) => visibleColumns.includes(config.field))
                        .map((config) => (
                            <Column key={config.field} field={config.field} header={config.header} body={config.body} sortable={config.sortable} style={config.style} />
                        ))}
                </DataTable>
            )}
        </Dialog>
    );
};

export default VehicleRegistrationHistoryDialog;



